import { FieldErrors, RegisterOptions, UseFormRegister } from 'react-hook-form';
import clsx from 'clsx';
import { ChangeEvent, ChangeEventHandler } from 'react';
import { convertPathToDotNotation, sanitize } from '@msc-b2c/shared-utils';

type InputProps = {
  id?: string;
  label: string;
  name: string;
  index: number;
  register: UseFormRegister<any>;
  errors?: FieldErrors;
  validation?: RegisterOptions;
  clearErrors?: any;
  placeholder: string;
  inputType?: string;
  inputMode?: 'text' | 'search' | 'none' | 'email' | 'tel' | 'url' | 'numeric' | 'decimal';
  disabled?: boolean;
  isLowerLabel?: boolean;
  preventCopyPaste?: boolean;
  styleOption?: string;
  handleInputChange?: ChangeEventHandler;
  styleOptions?: string;
  onFocus?: () => void;
  required?: boolean;
  clearErrorsOnFocus?: boolean;
  description?: string;
};

// The following component is an example of your existing Input Component
export const InputText = ({
  id,
  label,
  name,
  index,
  register,
  errors,
  validation,
  clearErrors,
  disabled,
  placeholder,
  inputType = 'text',
  inputMode,
  isLowerLabel,
  preventCopyPaste = false,
  styleOption,
  handleInputChange,
  styleOptions = '',
  onFocus,
  required = false,
  clearErrorsOnFocus = true,
  description,
}: InputProps) => {
  const fieldError = convertPathToDotNotation(errors, name);
  const errorMessage = fieldError?.message?.toString();
  const isErrorClass = fieldError ? 'error-input' : '';
  const isDisabled = disabled ? 'disabled-input' : '';
  const labelClass = isLowerLabel ? 'cmp-input__lowerlabel' : 'cmp-input__upperlabel';
  const inputId = `${name}-${index}`;
  const errorId = `${inputId}-error`;
  const descriptionId = `${inputId}-description`;
  const describedBy = errorMessage ? errorId : description ? descriptionId : undefined;

  const handleFocus = () => {
    if (clearErrorsOnFocus) {
      clearErrors?.(name);
    }
    onFocus?.();
  };

  // Get all the props from react-hook-form's register function
  const rhfProps = register(name, { ...validation });

  const combinedOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    // NOTE: `onChange={handleInputChange} overwrites react-hook-form's internal `onChange` handler.
    // This prevents RHF from updating its state, especially for values populated by browser autofill.

    // FIX: This combined handler ensures that RHF's `onChange` is always called first to update its internal state,
    // and then the component's custom `handleInputChange` prop (if provided) is also executed.

    rhfProps.onChange(e);
    handleInputChange?.(e);
  };

  return (
    <div
      id={id}
      className={`text ${labelClass} ${isErrorClass} ${isDisabled} ${styleOptions} ${styleOptions} ${name}`}
    >
      <div className="cmp-form-text text">
        {!isLowerLabel && (
          <label
            className={clsx('cmp-label', `${isErrorClass}`)}
            htmlFor={inputId}
          >
            {label}
          </label>
        )}
        <input
          className={'cmp-form-text__text react-input'}
          aria-invalid={fieldError ? 'true' : 'false'}
          aria-required={required ? true : undefined}
          aria-describedby={describedBy}
          type={inputType}
          inputMode={inputMode}
          id={inputId}
          placeholder={placeholder}
          disabled={disabled}
          {...rhfProps}
          onFocus={handleFocus}
          onPaste={(e) => preventCopyPaste && e.preventDefault()}
          onCopy={(e) => preventCopyPaste && e.preventDefault()}
          onCut={(e) => preventCopyPaste && e.preventDefault()}
          onChange={combinedOnChange}
        />
        {description && !errorMessage && (
          <span
            id={descriptionId}
            className="sr-only"
          >
            {description}
          </span>
        )}
        {errorMessage && (
          <div className="error-list-validation">
            <div
              id={errorId}
              role="alert"
              aria-live="polite"
              dangerouslySetInnerHTML={{ __html: sanitize(errorMessage) }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
